import type { ComponentType } from 'react';
import { useLingui } from '@lingui/react';
import BigCursorIcon from '../../icons/big-cursor.svg?react';
import BlueYellowIcon from '../../icons/blue-yellow.svg?react';
import ColorBlindnessIcon from '../../icons/color-blindness.svg?react';
import DefaultColorsIcon from '../../icons/default-colors.svg?react';
import DyslexiaFriendlyFontIcon from '../../icons/dyslexia-friendly-font.svg?react';
import EasyReadFontIcon from '../../icons/easy-read-font.svg?react';
import ExtraTextSpacingIcon from '../../icons/extra-text-spacing.svg?react';
import GrayscaleIcon from '../../icons/grayscale.svg?react';
import GreenRedIcon from '../../icons/green-red.svg?react';
import HighContrastIcon from '../../icons/high-contrast.svg?react';
import HighlightLinksIcon from '../../icons/highlight-links.svg?react';
import InvertedColorsIcon from '../../icons/inverted-colors.svg?react';
import KeyboardFocusIcon from '../../icons/keyboard-focus.svg?react';
import LargeClickableAreaIcon from '../../icons/large-clickable-area.svg?react';
import LargeTextIcon from '../../icons/large-text.svg?react';
import ReadingLineIcon from '../../icons/reading-line.svg?react';
import ReadingMaskIcon from '../../icons/reading-mask.svg?react';
import RedGreenIcon from '../../icons/red-green.svg?react';
import StopAnimationIcon from '../../icons/stop-animation.svg?react';
import type {
  ColorAndContrastSetting,
  NavigationSetting,
  TextAndReadabilitySetting
} from '../../types';

export type SettingsData = {
  label: string;
  icon: ComponentType;
};

export const useSettingsData = () => {
  const { i18n } = useLingui();

  const textAndReadabilitySettings: Record<
    TextAndReadabilitySetting,
    SettingsData
  > = {
    largeText: {
      label: i18n._('Large Text'),
      icon: LargeTextIcon
    },
    extraTextSpacing: {
      label: i18n._('Extra Text Spacing'),
      icon: ExtraTextSpacingIcon
    },
    easyReadFont: {
      label: i18n._('Easy-Read Font'),
      icon: EasyReadFontIcon
    },
    dyslexiaFriendlyFont: {
      label: i18n._('Dyslexia-Friendly Font'),
      icon: DyslexiaFriendlyFontIcon
    },
    readingMask: {
      label: i18n._('Reading Mask'),
      icon: ReadingMaskIcon
    },
    readingLine: {
      label: i18n._('Reading Line'),
      icon: ReadingLineIcon
    },
    stopAnimation: {
      label: i18n._('Stop Animation'),
      icon: StopAnimationIcon
    }
  };

  const navigationSettings: Record<NavigationSetting, SettingsData> = {
    keyboardFocus: {
      label: i18n._('Keyboard Focus'),
      icon: KeyboardFocusIcon
    },
    largeClickableAreas: {
      label: i18n._('Large Clickable Areas'),
      icon: LargeClickableAreaIcon
    },
    bigCursor: {
      label: i18n._('Big Cursor'),
      icon: BigCursorIcon
    },
    highlightLinks: {
      label: i18n._('Highlight Links'),
      icon: HighlightLinksIcon
    }
  };

  const colorAndContrastSettings: Record<
    ColorAndContrastSetting,
    SettingsData
  > = {
    'default-colors': {
      label: i18n._('Default Colors'),
      icon: DefaultColorsIcon
    },
    'high-contrast': {
      label: i18n._('High Contrast'),
      icon: HighContrastIcon
    },
    grayscale: {
      label: i18n._('Grayscale'),
      icon: GrayscaleIcon
    },
    'inverted-colors': {
      label: i18n._('Inverted Colors'),
      icon: InvertedColorsIcon
    },
    'color-blind': {
      label: i18n._('Color Blind'),
      icon: ColorBlindnessIcon
    },
    protanopia: {
      label: i18n._('Red-Green (Protanopia)'),
      icon: RedGreenIcon
    },
    deuteranopia: {
      label: i18n._('Green-Red (Deuteranopia)'),
      icon: GreenRedIcon
    },
    tritanopia: {
      label: i18n._('Blue-Yellow (Tritanopia)'),
      icon: BlueYellowIcon
    }
  };

  return {
    textAndReadabilitySettings,
    navigationSettings,
    colorAndContrastSettings
  };
};
